import classNames from "classnames";
import Image from "next/image";
import { useFormatter, useTranslations } from "next-intl";
import { useEffect, useMemo, useRef } from "react";

import IconChevronRight from "@/icons/icon-chevron-right";
import CrossCountry from "~/types/cross-country";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";

import Icon from "../common/icon";
import styles from "./product-color-selector.module.scss";

type Props = {
  product: CrossCountry.Product;
  current: CrossCountry.ChildrenProduct;
  onSelect: () => void;
  currentDialog?: "colors" | "notifyMe" | null;
  removeBorder?: boolean;
  disableBtn?: boolean;
  focusOnSelectorBtn?: boolean;
};

export default function ProductColorSelector(props: Props) {
  const t = useTranslations();
  const formatter = useFormatter();

  const numberOfColors = props.product.children?.length || 0;
  const shade = props.current;

  const displayName = useMemo(() => {
    const shadeNumber = shade.shade_number ? `${shade.shade_number} ` : "";
    const shadeNameOrDesc = shade.shade_name || shade.color || "";
    return `${shadeNumber}${shadeNameOrDesc}`;
  }, [shade]);

  const goccia = productUtils.getProductDroplet(props.current.product_media?.media)?.url;
  const colorSelectorBtn = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // a11y - focus on color selector button on panel closing
    if (props.focusOnSelectorBtn && props.currentDialog == null) {
      colorSelectorBtn.current?.focus();
    }
  }, [props.focusOnSelectorBtn, props.currentDialog]);

  return (
    <button
      className={classNames(styles.container, props.removeBorder ? styles.removeBorder : undefined)}
      onClick={() => props.onSelect()}
      aria-haspopup="dialog"
      aria-label={displayName + " " + t("a11y.more_colors", { number: numberOfColors - 1 })}
      ref={colorSelectorBtn}
    >
      <div className={styles.current}>
        {goccia ? (
          <Image
            src={goccia || imageUtils.transparentPixel}
            alt={displayName}
            width={16}
            height={16}
            className={styles.image}
            loader={imageUtils.evaImageLoader}
            aria-hidden
          />
        ) : null}
        <p className={styles.name}>{displayName}</p>
      </div>
      {numberOfColors > 1 &&
        (!props.disableBtn ? (
          <div className={styles.available}>
            <p>{formatter.number(numberOfColors - 1, { signDisplay: "always" })}</p>
            <Icon icon={IconChevronRight} width={16} height={16} aria-hidden />
          </div>
        ) : (
          <div
            className={classNames(styles.available, styles.noBtn)}
            aria-label={t("a11y.more_colors", { number: numberOfColors - 1 })}
          >
            <p>{formatter.number(numberOfColors - 1, { signDisplay: "always" })}</p>
          </div>
        ))}
    </button>
  );
}
